import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { beepCarico, beepScarico, beepError, unlockAudio } from "@/lib/sound";
import { ScanLine, Keyboard, Camera, ArrowUpCircle, ArrowDownCircle, CheckCircle2, XCircle, Fingerprint } from "lucide-react";

export default function ScannerBarcode() {
  const [metodo, setMetodo] = useState("hardware"); // hardware | camera
  const [azione, setAzione] = useState("carico"); // carico | scarico
  const [banner, setBanner] = useState(null); // {tipo, testo}
  const [scansioni, setScansioni] = useState([]);
  const [holding, setHolding] = useState(false);
  const [quantita, setQuantita] = useState(1);
  const inputRef = useRef(null);
  const [value, setValue] = useState("");
  const azioneRef = useRef(azione);
  const quantitaRef = useRef(1);
  const scannerRef = useRef(null);
  const bannerTimer = useRef(null);
  const holdingRef = useRef(false);
  const scannedThisHoldRef = useRef(false);

  useEffect(() => { azioneRef.current = azione; }, [azione]);
  useEffect(() => { quantitaRef.current = Math.max(1, parseInt(quantita, 10) || 1); }, [quantita]);

  const showBanner = (tipo, testo) => {
    setBanner({ tipo, testo });
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    bannerTimer.current = setTimeout(() => setBanner(null), 3000);
  };

  const processScan = useCallback(async (codice) => {
    const code = (codice || "").trim();
    if (!code) return;
    try {
      const { data } = await api.post("/scanner/scan", { codice: code, azione: azioneRef.current, quantita: quantitaRef.current });
      if (data.successo) {
        (azioneRef.current === "carico" ? beepCarico : beepScarico)();
        showBanner("success", data.messaggio + ` — Giacenza: ${data.quantita_attuale}` + (data.sotto_soglia ? " ⚠ Sotto Scorta!" : ""));
      } else {
        beepError();
        showBanner("error", data.messaggio);
      }
      setScansioni((prev) => [{ id: crypto.randomUUID(), code, ok: data.successo, msg: data.messaggio, t: new Date().toLocaleTimeString("it-IT") }, ...prev].slice(0, 8));
    } catch (e) {
      beepError();
      showBanner("error", "Errore: Codice a Barre Non Riconosciuto");
    }
  }, []);

  // Hardware mode: keep input focused
  useEffect(() => {
    if (metodo === "hardware") {
      const focus = () => inputRef.current?.focus();
      focus();
      const iv = setInterval(focus, 800);
      return () => clearInterval(iv);
    }
  }, [metodo]);

  // Camera mode: start/stop html5-qrcode
  useEffect(() => {
    let mounted = true;
    if (metodo === "camera") {
      (async () => {
        try {
          const { Html5Qrcode } = await import("html5-qrcode");
          if (!mounted) return;
          const scanner = new Html5Qrcode("html5-qr-code-full-region");
          scannerRef.current = scanner;
          await scanner.start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 250, height: 150 } },
            (decoded) => {
              // Scansione solo mentre il pulsante è premuto, e una sola volta per pressione
              if (!holdingRef.current || scannedThisHoldRef.current) return;
              scannedThisHoldRef.current = true;
              setHolding(false);
              holdingRef.current = false;
              processScan(decoded);
            },
            () => {}
          );
        } catch (e) {
          showBanner("error", "Impossibile accedere alla fotocamera. Verifica i permessi.");
        }
      })();
    }
    return () => {
      mounted = false;
      if (scannerRef.current) {
        scannerRef.current.stop().then(() => scannerRef.current?.clear()).catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [metodo, processScan]);

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      processScan(value);
      setValue("");
      inputRef.current?.focus();
    }
  };

  const startHold = () => {
    unlockAudio();
    scannedThisHoldRef.current = false;
    holdingRef.current = true;
    setHolding(true);
  };
  const endHold = () => {
    holdingRef.current = false;
    setHolding(false);
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-widest mb-1">
          <ScanLine className="w-4 h-4" /> Operazioni rapide
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 tracking-tight">Scanner Barcode</h1>
        <p className="text-slate-400 text-sm mt-1">Carico e scarico rapido dei capi neutri tramite scansione</p>
      </div>

      {/* Method selector */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <button data-testid="hardware-mode-btn" onClick={() => setMetodo("hardware")}
          className={`flex items-center justify-center gap-2.5 py-4 rounded-xl font-semibold transition-all ${metodo === "hardware" ? "bg-cyan-400/15 text-cyan-300 border-2 border-cyan-400/50" : "glass text-slate-400 border-2 border-transparent hover:text-slate-200"}`}>
          <Keyboard className="w-5 h-5" /> Usa Lettore Classico (Hardware)
        </button>
        <button data-testid="camera-mode-btn" onClick={() => setMetodo("camera")}
          className={`flex items-center justify-center gap-2.5 py-4 rounded-xl font-semibold transition-all ${metodo === "camera" ? "bg-cyan-400/15 text-cyan-300 border-2 border-cyan-400/50" : "glass text-slate-400 border-2 border-transparent hover:text-slate-200"}`}>
          <Camera className="w-5 h-5" /> Usa Fotocamera Smartphone
        </button>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button data-testid="btn-carico" onClick={() => setAzione("carico")}
          className={`flex items-center justify-center gap-3 py-6 rounded-xl text-lg font-bold transition-all ${azione === "carico" ? "bg-green-500 text-slate-900 shadow-lg shadow-green-500/30 scale-[1.02]" : "bg-green-500/20 text-green-300 hover:bg-green-500/30"}`}>
          <ArrowUpCircle className="w-7 h-7" /> CARICO (+{Math.max(1, parseInt(quantita, 10) || 1)})
        </button>
        <button data-testid="btn-scarico" onClick={() => setAzione("scarico")}
          className={`flex items-center justify-center gap-3 py-6 rounded-xl text-lg font-bold transition-all ${azione === "scarico" ? "bg-red-500 text-white shadow-lg shadow-red-500/30 scale-[1.02]" : "bg-red-500/20 text-red-300 hover:bg-red-500/30"}`}>
          <ArrowDownCircle className="w-7 h-7" /> SCARICO (-{Math.max(1, parseInt(quantita, 10) || 1)})
        </button>
      </div>

      {/* Quantity selector */}
      <div className="glass rounded-xl p-4 mb-5">
        <label className="text-xs uppercase tracking-widest text-slate-400">Quantità per scansione</label>
        <div className="mt-2 flex items-center gap-3">
          <button data-testid="qta-decrement" onClick={() => setQuantita((q) => Math.max(1, (parseInt(q, 10) || 1) - 1))}
            className="w-12 h-12 shrink-0 rounded-xl bg-slate-800 text-slate-200 text-2xl font-bold hover:bg-slate-700 transition-colors">−</button>
          <input
            data-testid="scan-quantita-input"
            type="number"
            min="1"
            value={quantita}
            onChange={(e) => setQuantita(e.target.value)}
            onBlur={() => setQuantita((q) => Math.max(1, parseInt(q, 10) || 1))}
            className="flex-1 min-w-0 text-center bg-slate-900/70 border-2 border-cyan-400/40 rounded-xl px-4 py-3 text-slate-100 text-2xl font-mono-tech focus:outline-none focus:border-cyan-400"
          />
          <button data-testid="qta-increment" onClick={() => setQuantita((q) => (parseInt(q, 10) || 1) + 1)}
            className="w-12 h-12 shrink-0 rounded-xl bg-slate-800 text-slate-200 text-2xl font-bold hover:bg-slate-700 transition-colors">+</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2" data-testid="qta-presets">
          {[1, 5, 10, 25, 50].map((n) => (
            <button key={n} onClick={() => setQuantita(n)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${(parseInt(quantita, 10) || 1) === n ? "bg-cyan-400/20 text-cyan-200 border border-cyan-400/50" : "bg-slate-800/60 text-slate-400 hover:text-slate-200"}`}>
              {n}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">Imposta la quantità e scansiona una sola volta: verrà applicata tutta in un colpo (niente scansioni ripetute).</p>
      </div>

      {/* Banner */}
      {banner && (
        <div data-testid="scan-status-banner"
          className={`mb-5 rounded-xl px-5 py-4 flex items-center gap-3 fade-in font-semibold ${banner.tipo === "success" ? "bg-green-500/15 border border-green-500/50 text-green-200" : "bg-red-500/15 border border-red-500/50 text-red-200"}`}>
          {banner.tipo === "success" ? <CheckCircle2 className="w-6 h-6 shrink-0" /> : <XCircle className="w-6 h-6 shrink-0" />}
          {banner.testo}
        </div>
      )}

      {/* Input area */}
      <div className="glass rounded-xl p-5 mb-5">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">
          Azione attiva: <span className={azione === "carico" ? "text-green-400" : "text-red-400"}>{azione === "carico" ? `CARICO +${Math.max(1, parseInt(quantita, 10) || 1)}` : `SCARICO -${Math.max(1, parseInt(quantita, 10) || 1)}`}</span>
        </div>
        {metodo === "hardware" ? (
          <div>
            <label className="text-sm text-slate-400">Scansiona con il lettore o digita il codice e premi Invio</label>
            <input
              ref={inputRef}
              data-testid="barcode-input"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="In attesa di scansione..."
              className="pulse-focus mt-2 w-full bg-slate-900/70 border-2 border-cyan-400/40 rounded-xl px-5 py-4 text-slate-100 text-lg font-mono-tech focus:outline-none focus:border-cyan-400"
            />
          </div>
        ) : (
          <div>
            <div id="html5-qr-code-full-region" data-testid="camera-region" className="w-full max-w-md mx-auto rounded-xl overflow-hidden" style={{ maxHeight: "60vh" }} />
            <button
              data-testid="hold-to-scan-btn"
              onPointerDown={(e) => { e.preventDefault(); startHold(); }}
              onPointerUp={endHold}
              onPointerLeave={endHold}
              onPointerCancel={endHold}
              onContextMenu={(e) => e.preventDefault()}
              style={{ touchAction: "none" }}
              className={`mt-4 w-full max-w-md mx-auto flex items-center justify-center gap-3 py-6 rounded-xl text-lg font-bold select-none transition-all ${holding ? "bg-cyan-400 text-slate-900 shadow-lg shadow-cyan-400/40 scale-[0.98]" : "bg-cyan-400/20 text-cyan-200 hover:bg-cyan-400/30"}`}
            >
              <Fingerprint className="w-7 h-7" /> {holding ? "SCANSIONE IN CORSO..." : "TIENI PREMUTO PER SCANSIONARE"}
            </button>
            <p className="text-center text-xs text-slate-500 mt-3">Inquadra il codice, poi tieni premuto il pulsante: viene registrata una sola scansione per ogni pressione. Rilascia e ripremi per la successiva.</p>
          </div>
        )}
      </div>

      {/* Recent scans */}
      {scansioni.length > 0 && (
        <div className="glass rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3">Ultime scansioni</h3>
          <div className="space-y-1.5" data-testid="scan-history">
            {scansioni.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-800/60 last:border-0">
                <div className="flex items-center gap-2">
                  {s.ok ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
                  <span className="font-mono-tech text-slate-300">{s.code}</span>
                </div>
                <span className="text-slate-500 text-xs">{s.t}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
